import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { WEEKDAY_ABBR, buildMonthGrids } from '../utils/calendarGrid';

// Price Intelligence (Phase 2) — recommend + explain.
//   M1: PI-02 Demand Calendar (fused 0-100 demand score + drivers).
//   M2: PI-01 Dashboard (recommendation queue, accept/reject) + PI-03 detail.
//   M3: PI-04 Demand Drivers (events from Event Calendar/PredictHQ), PI-05 Settings
//       (source health + signal weights + baseline/guardrails), CSV exports, a11y.
// Recommend-only — nothing here writes rates (that is Revenue Manager / Phase 3).
// Spec: docs/DEV_PRICEINTELLIGENCE_PHASE2.md §7.

const SIGNAL_META = {
  comp:        { label: 'Competitors', icon: '🏷️' },
  occupancy:   { label: 'Occupancy',   icon: '🛏️' },
  event:       { label: 'Event',       icon: '🎉' },
  seasonality: { label: 'Season / DOW', icon: '📅' },
  pace:        { label: 'Booking pace', icon: '🚀' },
};
const BASELINE_LABEL = { OWN_BAR: 'Own BAR', MARKET_MEDIAN: 'Market median', MARKET_BLEND: 'Market blend', LAST_ACCEPTED: 'Last accepted' };
const IMPACT_COLOR = { HIGH: '#b91c1c', MEDIUM: '#b45309', LOW: '#0369a1' };

function band(score) {
  if (score == null) return { label: 'No data', bg: 'var(--theme-surface-2, #f1f5f9)', fg: '#94a3b8', edge: '#e2e8f0' };
  if (score >= 75) return { label: 'High', bg: '#d1fae5', fg: '#047857', edge: '#6ee7b7' };
  if (score >= 60) return { label: 'Elevated', bg: '#fef3c7', fg: '#92400e', edge: '#fcd34d' };
  if (score >= 40) return { label: 'Moderate', bg: '#e0f2fe', fg: '#0369a1', edge: '#7dd3fc' };
  return { label: 'Soft', bg: '#f1f5f9', fg: '#64748b', edge: '#e2e8f0' };
}
const fmtINR = (n) => (n == null ? '—' : `₹${Math.round(Number(n)).toLocaleString('en-IN')}`);
const shortDateDow = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return { day: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), dow: d.toLocaleDateString('en-IN', { weekday: 'short' }) };
};
// Static explainer for the Matrix view's "Why these recommendations?" strip —
// maps each demand signal (+ Pace) onto the plain-language driver a hotelier
// would recognize, not a per-cell computed value.
const WHY_DRIVERS = [
  { icon: '📈', label: 'High Demand', sub: 'Strong demand & events' },
  { icon: '🚀', label: 'Pickup Acceleration', sub: 'Booking pace is higher' },
  { icon: '🏷️', label: 'Market Movement', sub: 'Competitors changing rates' },
  { icon: '🛏️', label: 'Low Availability', sub: 'Fewer rooms remaining' },
  { icon: '📊', label: 'Historical Trend', sub: 'Season / day-of-week pattern' },
];

const SIGNAL_ORDER = ['comp', 'occupancy', 'event', 'seasonality', 'pace'];
const DEMAND_HEADER_TOOLTIP = 'Demand (0-100): a weighted average of 5 signals — Competitors\' rates/availability, your Occupancy forecast, local Events, Seasonality/day-of-week, and Booking pace (this year vs last year at the same lead time). Occupancy counts most by default. 50 = neutral; above = stronger than average demand, below = softer. Click a row for this date\'s exact breakdown.';
const CONFIDENCE_HEADER_TOOLTIP = 'Confidence: the share of signal weight actually backed by data for this date — not a statistical margin of error. 100% = every enabled signal had data. Lower means one or more signals (often Occupancy, which counts most) had none, so the score leans on whatever signals ARE present.';

// Per-row breakdown, built from the same reasoning_json every recommendation
// already carries (no extra fetch) — gives the header tooltip's general
// explanation a concrete "here's what actually fed this number" for the date.
function demandRowTooltip(r) {
  const comps = r.reasoning_json?.components;
  if (!comps) return DEMAND_HEADER_TOOLTIP;
  const lines = SIGNAL_ORDER.map((k) => {
    const p = comps[k]; const label = SIGNAL_META[k].label;
    if (!p || !p.enabled) return `${label}: off`;
    if (!p.present || p.value == null) return `${label}: no data for this date (weight ${Number(p.weight).toFixed(1)} — excluded)`;
    return `${label}: ${Math.round(p.value)}/100 (weight ${Number(p.weight).toFixed(1)})`;
  });
  return `${DEMAND_HEADER_TOOLTIP}\n\nThis date:\n${lines.join('\n')}`;
}
function confidenceRowTooltip(r) {
  const comps = r.reasoning_json?.components;
  if (!comps) return CONFIDENCE_HEADER_TOOLTIP;
  const missing = SIGNAL_ORDER.filter((k) => comps[k]?.enabled && (!comps[k]?.present || comps[k]?.value == null)).map((k) => SIGNAL_META[k].label);
  const detail = missing.length
    ? `${missing.join(' and ')} had no data for this date — the score relies on the remaining signal(s) instead.`
    : 'Every enabled signal had data for this date.';
  return `${CONFIDENCE_HEADER_TOOLTIP}\n\nThis date: ${detail}`;
}

const STATUS_CHIP = {
  PENDING:  { bg: '#fef3c7', fg: '#92400e', label: 'Pending' },
  ACCEPTED: { bg: '#d1fae5', fg: '#047857', label: 'Accepted' },
  REJECTED: { bg: '#fee2e2', fg: '#b91c1c', label: 'Rejected' },
  EXPIRED:  { bg: '#f1f5f9', fg: '#64748b', label: 'Expired' },
};

// CSV export (client Blob) — non-financial grid export, consistent with Rate Shopper.
const toCsv = (rows, cols) => {
  const esc = (v) => `"${v == null ? '' : String(v).replace(/"/g, '""')}"`;
  const head = cols.map((c) => esc(c.label)).join(',');
  const body = rows.map((r) => cols.map((c) => esc(typeof c.key === 'function' ? c.key(r) : r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
};
const downloadCsv = (name, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
};

const btn = (kind) => ({
  padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  border: kind === 'primary' || kind === 'accept' || kind === 'reject' ? 'none' : '1px solid var(--theme-border, #e5e7eb)',
  background: kind === 'primary' ? 'var(--theme-primary, #0d8f83)' : kind === 'accept' ? '#059669' : kind === 'reject' ? '#dc2626' : 'var(--theme-surface, #fff)',
  color: kind === 'primary' || kind === 'accept' || kind === 'reject' ? '#fff' : 'var(--theme-text, #374151)',
});
const sel = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border, #e5e7eb)', background: 'var(--theme-surface, #fff)', color: 'var(--theme-text, #374151)', fontSize: 13 };
const banner = (kind) => ({
  padding: '9px 12px', borderRadius: 8, fontSize: 13, margin: '10px 0',
  background: kind === 'danger' ? '#fef2f2' : kind === 'warn' ? '#fffbeb' : '#ecfeff',
  border: `1px solid ${kind === 'danger' ? '#fecaca' : kind === 'warn' ? '#fde68a' : '#a5f3fc'}`,
  color: kind === 'danger' ? '#b91c1c' : kind === 'warn' ? '#92400e' : '#155e75',
});
const kpiBox = { flex: '1 1 140px', minWidth: 140, background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: '12px 14px' };
const card = { background: 'var(--theme-surface, #fff)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 12, padding: 16 };
const th = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', padding: '8px 10px', borderBottom: '1px solid var(--theme-border, #e5e7eb)', position: 'sticky', top: 0, background: 'var(--theme-surface, #fff)' };
const td = { padding: '8px 10px', borderBottom: '1px solid var(--theme-border-light, #f1f5f9)', fontSize: 13 };
// PI-01 Matrix view — card-style grid distinct from the flat List table above.
const matrixHeadCell = { padding: '10px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', background: 'var(--theme-surface-2, #f8fafc)', borderBottom: '1px solid var(--theme-border, #e5e7eb)' };
const matrixRowLabel = { padding: '10px 12px', fontSize: 12, fontWeight: 700, color: 'var(--theme-text, #374151)', position: 'sticky', left: 0, background: 'var(--theme-surface, #fff)', borderBottom: '1px solid var(--theme-border-light, #f1f5f9)', whiteSpace: 'nowrap' };
const matrixCell = { padding: '10px 8px', fontSize: 13, textAlign: 'center', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid var(--theme-border-light, #f1f5f9)' };
// Keyboard-activatable non-button element (a11y).
const keyActivate = (fn) => ({ role: 'button', tabIndex: 0, onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } } });

// Demand Drivers tiles — Events (summarized) + Pickup (already computed backend-
// side, never surfaced before now) + Pace (new YoY booking-pace metric). Market
// Sentiment is deliberately not a tile here — no genuine data source backs it.
function DriverTiles({ drivers, compact }) {
  if (!drivers) return null;
  const eventCount = drivers.events?.length || 0;
  const eventText = eventCount === 0 ? 'No events in window' : eventCount === 1 ? drivers.events[0].event_name : `${eventCount} events impacting demand`;
  const pace = drivers.pace || {};
  const pickup = drivers.pickup || {};
  const tiles = [
    { label: 'Events', value: eventCount, sub: eventText },
    { label: 'Pace', value: pace.pct_vs_last_year != null ? `${pace.pct_vs_last_year > 0 ? '+' : ''}${pace.pct_vs_last_year}%` : '—', sub: pace.label || 'Not enough history for a comparison' },
    { label: 'Pickup', value: pickup.label || '—', sub: pickup.avg_pickup_pct != null ? `${pickup.avg_pickup_pct}% on the books (forecast ${pickup.avg_occupancy_pct ?? '—'}%)` : 'No occupancy forecast data yet' },
  ];
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: compact ? 12 : 16 }}>
      {tiles.map((t) => (
        <div key={t.label} style={{ ...kpiBox, minWidth: compact ? 150 : 180 }}>
          <div style={{ fontSize: compact ? 18 : 20, fontWeight: 700 }}>{t.value}</div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{t.label}</div>
          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 2 }}>{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

function Chip({ status }) {
  const c = STATUS_CHIP[status] || STATUS_CHIP.PENDING;
  return <span style={{ background: c.bg, color: c.fg, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>{c.label}</span>;
}

function PriceIntelligence({ token, selectedProperty, onOpenInRms, onOpenInRateShopper }) {
  const [tab, setTab] = useState('dashboard');
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState('');
  const [meal, setMeal] = useState('EP');
  const [days, setDays] = useState(30);
  const [cal, setCal] = useState([]);
  const [health, setHealth] = useState(null);
  const [signals, setSignals] = useState([]);
  const [recos, setRecos] = useState([]);
  const [ownOtaRates, setOwnOtaRates] = useState([]);
  const [drivers, setDrivers] = useState(null);      // { city, events }
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [demandDetail, setDemandDetail] = useState(null);
  const [reco, setReco] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [outcomes, setOutcomes] = useState(null); // REQ-3 accepted-vs-not outcome summary
  const [recoView, setRecoView] = useState('matrix'); // 'list' | 'matrix' — same data, transposed
  const [overrideRate, setOverrideRate] = useState(''); // PI-03 editable override, synced to reco on open
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [narrativeBusy, setNarrativeBusy] = useState(false);

  // Reset the override input whenever the drawer opens for a (possibly new) reco.
  useEffect(() => {
    setOverrideRate(reco?.recommended_rate != null ? String(Math.round(reco.recommended_rate)) : '');
  }, [reco]);

  const api = useMemo(() => async (path, opts = {}) => {
    const res = await fetch(`/api/v1/price-intelligence${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
        'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '',
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [token, selectedProperty]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const r = await api('/own-rooms');
        setRooms(r.rooms || []);
        if (r.rooms?.length) setRoom((prev) => prev || String(r.rooms[0].own_room_id));
      } catch (e) { setError(e.message); }
    })();
  }, [selectedProperty, api]);

  const loadRecos = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try { const r = await api(`/recommendations?days=${days}&meal=${meal}${room ? `&room=${room}` : ''}`); setRecos(r.recommendations || []); }
    catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api, days, meal, room, selectedProperty]);

  // "Your Hotel" row (Matrix view) — own listing's live rate per OTA channel.
  // Best-effort: a provider that hasn't started sending own-hotel rows yet
  // just yields an empty list, so the row quietly shows "—" instead of erroring.
  const loadOwnOtaRates = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try { const r = await api(`/own-ota-rates?days=${days}`); setOwnOtaRates(r.rates || []); }
    catch { setOwnOtaRates([]); }
  }, [api, days, selectedProperty]);

  const loadCalendar = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const [c, h] = await Promise.all([api(`/demand-calendar?days=${days}${room ? `&room=${room}` : ''}`), api('/signals/health')]);
      setCal(c.days || []); setHealth(h);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api, days, room, selectedProperty]);

  const loadDrivers = useCallback(async (silent) => {
    if (!selectedProperty?.id) return;
    if (!silent) { setLoading(true); setError(''); }
    try { const d = await api(`/demand-drivers?days=${Math.max(days, 60)}${room ? `&room=${room}` : ''}`); setDrivers(d); }
    catch (e) { if (!silent) setError(e.message); } finally { if (!silent) setLoading(false); }
  }, [api, days, room, selectedProperty]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, c, o] = await Promise.all([api('/signals/config'), api('/signals/health'), api('/reco-config'), api('/outcomes/summary?days=30').catch(() => null)]);
      setSignals(s.signals || []); setHealth(h);
      setCfg({ ...c, blackout_text: (c.blackout_dates || []).join(', ') });
      setOutcomes(o);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    if (tab === 'dashboard') { loadRecos(); loadDrivers(true); loadOwnOtaRates(); }
    else if (tab === 'calendar') loadCalendar();
    else if (tab === 'drivers') loadDrivers();
    else if (tab === 'settings') loadSettings();
  }, [tab, loadRecos, loadCalendar, loadDrivers, loadSettings, loadOwnOtaRates]);

  const compute = async () => {
    setBusy('compute'); setError(''); setMessage('');
    try { const r = await api('/demand/compute', { method: 'POST', body: JSON.stringify({ days }) }); setMessage(`Computed ${r.written} demand rows.`); await loadCalendar(); }
    catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const generate = async () => {
    setBusy('generate'); setError(''); setMessage('');
    try {
      const r = await api('/generate', { method: 'POST', body: JSON.stringify({ days, meal }) });
      // When nothing was generated the counters say nothing useful — the REASON
      // is the whole message. Show that instead of four zeros.
      if (r.error) setError(r.error);
      else if (r.note) setMessage(r.note);
      else {
        setMessage(`Generated ${r.written ?? 0} · ${r.preserved ?? 0} kept · ${r.clamped ?? 0} clamped · ${r.skipped ?? 0} skipped (no demand).`);
      }
      await loadRecos();
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const act = async (id, kind) => {
    try { await api(`/recommendations/${id}/${kind}`, { method: 'POST' }); await loadRecos(); if (reco?.id === id) setReco(null); }
    catch (e) { setError(e.message); }
  };
  // PI-03 editable override — the hotelier can adjust the recommended rate
  // before applying; guardrails still enforce sane bounds downstream in RMS.
  const saveOverride = async (id) => {
    const rate = Number(overrideRate);
    if (!Number.isFinite(rate) || rate <= 0) { setError('Enter a valid rate.'); return; }
    setOverrideBusy(true); setError('');
    try {
      const r = await api(`/recommendations/${id}/override`, { method: 'POST', body: JSON.stringify({ rate }) });
      setReco((prev) => (prev && prev.id === id ? { ...prev, recommended_rate: r.recommended_rate, delta_pct: r.delta_pct } : prev));
      await loadRecos();
      setMessage('Override saved.');
    } catch (e) { setError(e.message); } finally { setOverrideBusy(false); }
  };
  // Optional LLM narrative — opt-in per recommendation (never called for a
  // whole /generate batch), cached server-side so repeat opens are free.
  const generateNarrative = async (id) => {
    setNarrativeBusy(true); setError('');
    try {
      const r = await api(`/recommendations/${id}/narrate`, { method: 'POST' });
      setReco((prev) => (prev && prev.id === id ? { ...prev, reasoning_json: { ...prev.reasoning_json, llm_narrative: { text: r.narrative } } } : prev));
    } catch (e) { setError(e.message); } finally { setNarrativeBusy(false); }
  };
  // Never writes a rate itself — accepts the recommendation, then asks RMS's own
  // guardrailed pipeline to turn it into a decision. `decision: null` means RMS
  // couldn't create one (not licensed / no rate plan mapped) — shown as `reason`,
  // never a fake success.
  const applyRecommendation = async (id) => {
    setBusy(`apply-${id}`); setError(''); setMessage('');
    try {
      const source = recos.find((r) => String(r.id) === String(id)) || reco || topPendingReco;
      const r = await api(`/recommendations/${id}/apply`, { method: 'POST' });
      await loadRecos();
      if (r.decision && typeof onOpenInRms === 'function') {
        // Real hand-off: land the user on RMS's own Approve/Apply button for this
        // exact decision — Price Intelligence never writes the rate itself.
        onOpenInRms({
          stay_date: source?.stay_date, room: source?.room_type_id, room_name: source?.room_name,
          meal: source?.meal_plan_code, decisionId: r.decision.id,
        });
        return;
      }
      if (reco?.id === id) setReco(null);
      if (r.decision) setMessage(`Sent to Revenue Manager — decision #${r.decision.id} (${r.decision.status}) at ${fmtINR(r.decision.clamped_rate)}. Approve/Apply it there.`);
      else setError(r.reason || 'Recommendation accepted, but Revenue Manager could not create a decision.');
    } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  const acceptAll = async () => {
    try { const r = await api('/recommendations/accept-all', { method: 'POST', body: JSON.stringify({ meal, room: room || undefined }) }); setMessage(`Accepted ${r.accepted} pending.`); await loadRecos(); }
    catch (e) { setError(e.message); }
  };
  const saveConfig = async () => {
    try {
      const blackout = (cfg.blackout_text || '').split(',').map((s) => s.trim()).filter(Boolean);
      await api('/reco-config', { method: 'POST', body: JSON.stringify({ ...cfg, blackout_dates: blackout }) });
      setMessage('Config saved — click Generate on Recommendations to apply.');
    } catch (e) { setError(e.message); }
  };
  const saveSignal = async (signal_key, patch) => {
    try {
      const cur = signals.find((s) => s.signal_key === signal_key) || {};
      await api('/signals/config', { method: 'POST', body: JSON.stringify({ signal_key, enabled: cur.enabled, weight: cur.weight, ...patch }) });
      setSignals((prev) => prev.map((x) => x.signal_key === signal_key ? { ...x, ...patch } : x));
      setMessage('Signal updated — recompute + regenerate to apply.');
    } catch (e) { setError(e.message); }
  };

  const exportRecos = () => downloadCsv('price-intelligence-recommendations.csv', toCsv(recos, [
    { label: 'Date', key: 'stay_date' }, { label: 'Room', key: (r) => r.room_name || r.room_type_id }, { label: 'Meal', key: 'meal_plan_code' },
    { label: 'Baseline', key: 'baseline_rate' }, { label: 'Recommended', key: 'recommended_rate' }, { label: 'Delta %', key: 'delta_pct' },
    { label: 'Demand', key: 'demand_score' }, { label: 'Confidence', key: 'confidence' }, { label: 'Status', key: 'status' }, { label: 'Guardrail', key: 'guardrail_bound' },
  ]));
  const exportCalendar = () => downloadCsv('price-intelligence-demand.csv', toCsv(cal, [
    { label: 'Date', key: 'stay_date' }, { label: 'Demand', key: 'demand_score' }, { label: 'Confidence', key: 'confidence' },
    { label: 'Top driver', key: 'top_driver' }, { label: 'Market median', key: 'comp_median' }, { label: 'Occupancy', key: 'occupancy_on_books' },
  ]));

  const roomName = rooms.find((r) => String(r.own_room_id) === String(room))?.name || '';

  const recoKpis = useMemo(() => {
    const pending = recos.filter((r) => r.status === 'PENDING');
    const accepted = recos.filter((r) => r.status === 'ACCEPTED');
    const avgDelta = pending.length ? Math.round(pending.reduce((a, r) => a + (Number(r.delta_pct) || 0), 0) / pending.length) : null;
    const avgConf = recos.length ? Math.round(recos.reduce((a, r) => a + (Number(r.confidence) || 0), 0) / recos.length) : null;
    return { pending: pending.length, accepted: accepted.length, avgDelta, avgConf, clamped: recos.filter((r) => r.guardrail_bound).length };
  }, [recos]);
  const topPendingReco = useMemo(() => {
    const pending = recos.filter((r) => r.status === 'PENDING').sort((a, b) => a.stay_date.localeCompare(b.stay_date));
    return pending[0] || null;
  }, [recos]);
  // Matrix view: same recommendations, transposed to dates-as-columns. Assumes
  // one row per (date, room, meal) — already true since the dashboard's own
  // room/meal filters narrow `recos` to a single room+meal at a time.
  const recoDates = useMemo(() => [...new Set(recos.map((r) => r.stay_date))].sort(), [recos]);
  const recoByDate = useMemo(() => new Map(recos.map((r) => [r.stay_date, r])), [recos]);
  // "Your Hotel" row: per date, the list of {ota_channel, rate_amount, is_sold_out}
  // our own listing shows — independent of room/meal, since OTAs don't report
  // which internal room type they quoted.
  const ownOtaByDate = useMemo(() => {
    const m = new Map();
    for (const r of ownOtaRates) (m.get(r.stay_date) || m.set(r.stay_date, []).get(r.stay_date)).push(r);
    return m;
  }, [ownOtaRates]);
  const calKpis = useMemo(() => {
    const scored = cal.filter((d) => d.demand_score != null);
    return {
      avg: scored.length ? Math.round(scored.reduce((a, d) => a + d.demand_score, 0) / scored.length) : null,
      conf: scored.length ? Math.round(scored.reduce((a, d) => a + (d.confidence || 0), 0) / scored.length) : null,
      high: scored.filter((d) => d.demand_score >= 60).length,
    };
  }, [cal]);

  if (!selectedProperty?.id) return <div style={{ padding: 20 }}>Select a property to view Price Intelligence.</div>;

  return (
    <div style={{ padding: 20 }}>
      {/* Header + controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Price Intelligence</h2>
          <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>Explainable price recommendations from fused demand signals — recommend-only, human accepts.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {(tab === 'dashboard' || tab === 'calendar') && (
            <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} title="Own room type" aria-label="Room type">
              {rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}
            </select>
          )}
          {tab === 'dashboard' && (
            <select value={meal} onChange={(e) => setMeal(e.target.value)} style={sel} title="Meal plan" aria-label="Meal plan">
              {['EP', 'CP', 'MAP', 'AP'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          {tab !== 'settings' && (
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel} title="Horizon" aria-label="Horizon days">
              {[30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          )}
          {tab === 'dashboard' && <button onClick={generate} style={btn('primary')} disabled={busy === 'generate'}>{busy === 'generate' ? 'Generating…' : '✨ Generate'}</button>}
          {tab === 'calendar' && <button onClick={compute} style={btn('primary')} disabled={busy === 'compute'}>{busy === 'compute' ? 'Computing…' : '⟳ Compute demand'}</button>}
        </div>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Price Intelligence views" style={{ display: 'flex', gap: 6, margin: '12px 0', borderBottom: '1px solid var(--theme-border, #e5e7eb)', paddingBottom: 8, flexWrap: 'wrap' }}>
        {[['dashboard', 'Recommendations'], ['calendar', 'Demand Calendar'], ['drivers', 'Demand Drivers'], ['settings', 'Settings']].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} style={btn(tab === k ? 'primary' : 'ghost')}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')} role="alert">{error} <span {...keyActivate(() => setError(''))} onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {message && <div style={banner('ok')}>{message} <span {...keyActivate(() => setMessage(''))} onClick={() => setMessage('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {/* ── PI-01 Dashboard ── */}
      {tab === 'dashboard' && (
        <>
          <DriverTiles drivers={drivers} compact />
          {topPendingReco && (
            <div style={{ ...card, marginBottom: 12, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center', minWidth: 130 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: Number(topPendingReco.delta_pct) > 0 ? '#047857' : Number(topPendingReco.delta_pct) < 0 ? '#b91c1c' : '#64748b' }}>
                  {topPendingReco.delta_pct != null ? `${Number(topPendingReco.delta_pct) > 0 ? '+' : ''}${Number(topPendingReco.delta_pct).toFixed(0)}%` : '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>{Number(topPendingReco.delta_pct) >= 0 ? 'Increase Recommended' : 'Decrease Recommended'}</div>
              </div>
              <div style={{ flex: '1 1 260px', fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{topPendingReco.stay_date} · {topPendingReco.room_name || topPendingReco.room_type_id} · {topPendingReco.meal_plan_code}</div>
                {topPendingReco.reasoning_json?.narrative && <div style={{ color: 'var(--theme-text-muted)', marginTop: 4 }}>{topPendingReco.reasoning_json.narrative}</div>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => applyRecommendation(topPendingReco.id)} style={btn('primary')} disabled={busy === `apply-${topPendingReco.id}`}>{busy === `apply-${topPendingReco.id}` ? 'Applying…' : 'Apply Recommendation'}</button>
                <button onClick={() => setReco(topPendingReco)} style={btn('ghost')}>View Details →</button>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{recoKpis.pending}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Pending</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{recoKpis.avgDelta != null ? `${recoKpis.avgDelta > 0 ? '+' : ''}${recoKpis.avgDelta}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg recommended Δ</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{recoKpis.accepted}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Accepted</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{recoKpis.avgConf != null ? `${recoKpis.avgConf}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg confidence</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{recoKpis.clamped}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Guardrail-clamped</div></div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={acceptAll} style={btn('accept')} disabled={!recoKpis.pending}>✓ Accept all pending ({recoKpis.pending})</button>
            <button onClick={() => setTab('settings')} style={btn('ghost')}>⚙ Baseline & guardrails</button>
            <button onClick={exportRecos} style={btn('ghost')} disabled={!recos.length}>⭳ CSV</button>
            <div style={{ display: 'flex', marginLeft: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 8, overflow: 'hidden' }}>
              <button onClick={() => setRecoView('list')} style={{ ...btn(recoView === 'list' ? 'primary' : 'ghost'), borderRadius: 0, border: 'none' }}>List</button>
              <button onClick={() => setRecoView('matrix')} style={{ ...btn(recoView === 'matrix' ? 'primary' : 'ghost'), borderRadius: 0, border: 'none' }}>Matrix</button>
            </div>
          </div>
          {loading ? <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Loading…</div>
            : recos.length === 0 ? <div style={{ padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>No recommendations yet for {roomName} · {meal}. Compute demand (Demand Calendar), then <strong>✨ Generate</strong>.</div>
            : recoView === 'matrix' ? (
              <>
                <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 14, boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: 760 }}>
                    <thead>
                      <tr>
                        <th style={{ ...matrixHeadCell, position: 'sticky', left: 0, zIndex: 2, textAlign: 'left', minWidth: 130 }}>Date</th>
                        {recoDates.map((dt) => {
                          const { day, dow } = shortDateDow(dt);
                          return (
                            <th key={dt} style={{ ...matrixHeadCell, textAlign: 'center', minWidth: 96 }}>
                              <div>{dow}</div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--theme-text, #1f2937)' }}>{day}</div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={matrixRowLabel}>Current Rate</td>
                        {recoDates.map((dt) => <td key={dt} style={matrixCell}>{fmtINR(recoByDate.get(dt)?.baseline_rate)}</td>)}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel} title="Our own live rate on each OTA, as shopped by the Rate Data Service">Your Hotel — by OTA</td>
                        {recoDates.map((dt) => {
                          const channels = ownOtaByDate.get(dt) || [];
                          return (
                            <td key={dt} style={{ ...matrixCell, padding: '6px 4px' }}>
                              {channels.length === 0 ? <span style={{ color: 'var(--theme-text-muted)' }}>—</span> : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                                  {channels.map((c) => (
                                    <span key={c.ota_channel} title={c.ota_channel} style={{
                                      display: 'flex', justifyContent: 'space-between', gap: 6, width: '100%', maxWidth: 100,
                                      fontSize: 10.5, padding: '1px 6px', borderRadius: 6,
                                      background: c.is_sold_out ? 'var(--theme-surface-2, #f1f5f9)' : '#eef2ff',
                                      color: c.is_sold_out ? 'var(--theme-text-muted)' : '#3730a3',
                                    }}>
                                      <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.ota_channel}</span>
                                      <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{c.is_sold_out ? 'Sold' : fmtINR(c.rate_amount)}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel}>Market Median</td>
                        {recoDates.map((dt) => <td key={dt} style={{ ...matrixCell, color: 'var(--theme-text-muted)' }}>{fmtINR(recoByDate.get(dt)?.comp_median)}</td>)}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel}>Recommended Rate</td>
                        {recoDates.map((dt) => {
                          const r = recoByDate.get(dt);
                          const changed = r?.delta_pct != null && Math.abs(Number(r.delta_pct)) >= 0.1;
                          return (
                            <td key={dt} style={{ ...matrixCell, padding: '6px 4px' }}>
                              <span style={{
                                display: 'inline-block', minWidth: 62, padding: '4px 8px', borderRadius: 8, fontWeight: 800,
                                background: changed ? '#d1fae5' : 'transparent', color: changed ? '#047857' : 'var(--theme-text, #1f2937)',
                              }}>
                                {fmtINR(r?.recommended_rate)}
                              </span>
                              {r?.guardrail_bound && <span title={`Clamped: ${r.guardrail_bound}`} style={{ marginLeft: 4, color: '#d97706' }}>⚑</span>}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel}>vs Current</td>
                        {recoDates.map((dt) => { const r = recoByDate.get(dt); const up = Number(r?.delta_pct) > 0; return <td key={dt} style={{ ...matrixCell, fontWeight: 700, color: up ? '#047857' : Number(r?.delta_pct) < 0 ? '#b91c1c' : 'var(--theme-text-muted)' }}>{r?.delta_pct != null ? `${up ? '+' : ''}${Number(r.delta_pct).toFixed(1)}%` : '—'}</td>; })}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel}>Demand Score</td>
                        {recoDates.map((dt) => {
                          const r = recoByDate.get(dt); const b = band(r?.demand_score);
                          return (
                            <td key={dt} style={{ ...matrixCell, padding: '6px 4px' }} title={r ? demandRowTooltip(r) : ''}>
                              {r?.demand_score != null ? (
                                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', background: b.bg, color: b.fg, borderRadius: 10, padding: '4px 10px', minWidth: 56 }}>
                                  <span style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.1 }}>{Math.round(r.demand_score)}</span>
                                  <span style={{ fontSize: 10, fontWeight: 600 }}>{b.label}</span>
                                </span>
                              ) : '—'}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td style={matrixRowLabel}>Opportunity</td>
                        {recoDates.map((dt) => { const r = recoByDate.get(dt); const ref = r?.current_rate ?? r?.baseline_rate; const opp = (r?.recommended_rate != null && ref != null) ? Math.round(r.recommended_rate - ref) : null; return <td key={dt} style={{ ...matrixCell, fontWeight: 700, color: opp > 0 ? '#047857' : opp < 0 ? '#b91c1c' : 'var(--theme-text-muted)' }}>{opp != null ? `${opp > 0 ? '+' : ''}${fmtINR(opp)}` : '—'}</td>; })}
                      </tr>
                      <tr>
                        <td style={{ ...matrixRowLabel, borderBottom: 'none' }}>Action</td>
                        {recoDates.map((dt) => {
                          const r = recoByDate.get(dt);
                          const noChange = r?.delta_pct != null && Math.abs(Number(r.delta_pct)) < 0.1;
                          return (
                            <td key={dt} style={{ ...matrixCell, borderBottom: 'none' }}>
                              {!r || (r.status === 'PENDING' && noChange) ? <span style={{ color: 'var(--theme-text-muted)' }}>-</span>
                                : r.status === 'PENDING'
                                  ? <button onClick={() => applyRecommendation(r.id)} style={{ ...btn('primary'), padding: '5px 14px', fontSize: 12, borderRadius: 8 }} disabled={busy === `apply-${r.id}`}>{busy === `apply-${r.id}` ? '…' : 'Apply'}</button>
                                  : <Chip status={r.status} />}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                  <h3 style={{ fontSize: 14, margin: '0 0 -2px', width: '100%' }}>Why these recommendations?</h3>
                  {WHY_DRIVERS.map((w) => (
                    <div key={w.label} style={{ ...card, flex: '1 1 150px', minWidth: 150, textAlign: 'center', padding: '12px 10px' }}>
                      <div style={{ fontSize: 20 }}>{w.icon}</div>
                      <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>{w.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>{w.sub}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                  <thead><tr>
                    {['Date', 'Room · Meal', 'Baseline', 'Recommended', 'Δ'].map((h) => <th key={h} style={th}>{h}</th>)}
                    <th style={th}>Demand <span title={DEMAND_HEADER_TOOLTIP} style={{ cursor: 'help', color: '#94a3b8', fontWeight: 400 }} aria-label="How Demand is calculated">ⓘ</span></th>
                    <th style={th}>Conf. <span title={CONFIDENCE_HEADER_TOOLTIP} style={{ cursor: 'help', color: '#94a3b8', fontWeight: 400 }} aria-label="How Confidence is calculated">ⓘ</span></th>
                    {['Status', ''].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {recos.map((r) => {
                      const up = Number(r.delta_pct) > 0;
                      return (
                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setReco(r)}
                          aria-label={`${r.stay_date} ${r.room_name || ''} ${r.meal_plan_code}: recommend ${fmtINR(r.recommended_rate)}, ${r.status}`} {...keyActivate(() => setReco(r))}>
                          <td style={td}>{r.stay_date}</td>
                          <td style={td}>{r.room_name || r.room_type_id} · {r.meal_plan_code}</td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtINR(r.baseline_rate)}</td>
                          <td style={{ ...td, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtINR(r.recommended_rate)}{r.guardrail_bound && <span title={`Clamped: ${r.guardrail_bound}`} style={{ marginLeft: 5, color: '#d97706' }}>⚑</span>}</td>
                          <td style={{ ...td, color: up ? '#047857' : Number(r.delta_pct) < 0 ? '#b91c1c' : '#64748b', fontVariantNumeric: 'tabular-nums' }}>{r.delta_pct != null ? `${up ? '+' : ''}${Number(r.delta_pct).toFixed(1)}%` : '—'}</td>
                          <td style={td} title={demandRowTooltip(r)}><span style={{ background: band(r.demand_score).bg, color: band(r.demand_score).fg, borderRadius: 6, padding: '1px 7px', fontWeight: 700, fontSize: 12, cursor: 'help' }}>{r.demand_score != null ? Math.round(r.demand_score) : '—'}</span></td>
                          <td style={{ ...td, color: 'var(--theme-text-muted)', cursor: 'help' }} title={confidenceRowTooltip(r)}>{r.confidence != null ? `${Math.round(r.confidence)}%` : '—'}</td>
                          <td style={td}><Chip status={r.status} /></td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            {r.status === 'PENDING'
                              ? <><button onClick={() => act(r.id, 'accept')} aria-label="Accept" style={{ ...btn('accept'), padding: '4px 9px', marginRight: 5 }}>✓</button><button onClick={() => act(r.id, 'reject')} aria-label="Reject" style={{ ...btn('reject'), padding: '4px 9px' }}>✕</button></>
                              : <span style={{ fontSize: 11, color: '#94a3b8' }}>{r.acted_by || ''}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </>
      )}

      {/* ── PI-02 Demand Calendar ── */}
      {tab === 'calendar' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{calKpis.avg ?? '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg demand ({roomName})</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{calKpis.high}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Elevated+ days (≥60)</div></div>
            <div style={kpiBox}><div style={{ fontSize: 22, fontWeight: 700 }}>{calKpis.conf != null ? `${calKpis.conf}%` : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Avg confidence</div></div>
            <div style={{ ...kpiBox, display: 'flex', alignItems: 'center' }}><button onClick={exportCalendar} style={btn('ghost')} disabled={!cal.length}>⭳ CSV</button></div>
          </div>
          {loading ? <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Loading…</div>
            : cal.length === 0 ? <div style={{ padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>No demand data yet. Click <strong>⟳ Compute demand</strong>.</div>
            : buildMonthGrids(cal).map((mg) => (
              <div key={mg.key} style={{ marginBottom: 22 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--theme-text)' }}>{mg.label}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                  {WEEKDAY_ABBR.map((w) => (
                    <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', padding: '2px 0 6px' }}>{w}</div>
                  ))}
                  {mg.cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    if (d._outsideHorizon) {
                      const outDayNum = Number(d.stay_date.slice(8, 10));
                      return (
                        <div key={i} style={{ minHeight: 78, borderRadius: 10, border: '1px solid var(--theme-border, #e2e8f0)', opacity: 0.4, padding: '8px 9px' }}>
                          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', fontWeight: 600 }}>{outDayNum}</div>
                        </div>
                      );
                    }
                    const b = band(d.demand_score); const drv = d.top_driver ? SIGNAL_META[d.top_driver] : null;
                    const dayNum = Number(d.stay_date.slice(8, 10));
                    return (
                      <div key={i} aria-label={`${d.stay_date} demand ${d.demand_score ?? 'n/a'} ${b.label}`} {...keyActivate(() => setDemandDetail(d))}
                        onClick={() => setDemandDetail(d)}
                        style={{ background: b.bg, border: `1px solid ${b.edge}`, borderRadius: 10, padding: '8px 9px', cursor: 'pointer', minHeight: 90, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ fontSize: 11, color: b.fg, fontWeight: 600 }}>{dayNum}</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: b.fg, fontVariantNumeric: 'tabular-nums' }}>{d.demand_score != null ? Math.round(d.demand_score) : '—'}</div>
                        <div style={{ fontSize: 10, color: b.fg, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }} title="Competitor market median for this date">
                          mkt {d.comp_median != null ? fmtINR(d.comp_median) : '—'}{d.comp_rank ? ` · #${d.comp_rank}` : ''}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                          <span style={{ fontSize: 10, color: b.fg }}>{drv ? `${drv.icon} ${drv.label}` : b.label}</span>
                          {d.confidence != null && <span style={{ fontSize: 10, color: b.fg, opacity: 0.8 }}>{Math.round(d.confidence)}%</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </>
      )}

      {/* ── PI-04 Demand Drivers ── */}
      {tab === 'drivers' && (
        <div>
          <DriverTiles drivers={drivers} />
          <div style={{ fontSize: 13, color: 'var(--theme-text-muted)', marginBottom: 12 }}>
            Events & holidays lifting demand in <strong>{drivers?.city || 'this city'}</strong>, from the Event Calendar. These feed the <em>event</em> signal in the demand score.
          </div>
          {loading ? <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Loading…</div>
            : !drivers?.events?.length ? (
              <div style={{ padding: 24, background: 'var(--theme-surface-2, #f1f5f9)', borderRadius: 10, color: 'var(--theme-text-muted)', textAlign: 'center' }}>
                No events found for {drivers?.city || 'this city'} in the window. Ask your admin to configure the Event Calendar feed for this city to enrich the event signal.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {drivers.events.map((ev, i) => (
                  <div key={i} style={{ ...card, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px' }}>
                    <div style={{ textAlign: 'center', minWidth: 58 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: band(50 + ev.demand_lift).fg }}>+{ev.demand_lift}</div>
                      <div style={{ fontSize: 10, color: 'var(--theme-text-muted)' }}>lift</div>
                    </div>
                    <div style={{ flex: '1 1 auto' }}>
                      <div style={{ fontWeight: 600 }}>{ev.event_name} {ev.is_nearby && <span style={{ fontSize: 10, background: '#ecfeff', color: '#155e75', borderRadius: 20, padding: '1px 7px', marginLeft: 4 }}>nearby</span>}</div>
                      <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
                        {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` → ${ev.end_date}` : ''} · {ev.nights} night{ev.nights > 1 ? 's' : ''}
                        {ev.event_type ? ` · ${ev.event_type}` : ''}
                        {ev.phq_attendance ? ` · ${Number(ev.phq_attendance).toLocaleString('en-IN')} attending` : ''}
                        {ev.predicted_spend_accommodation ? ` · ${fmtINR(ev.predicted_spend_accommodation)} lodging spend` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {ev.expected_impact && <div style={{ fontSize: 12, fontWeight: 700, color: IMPACT_COLOR[String(ev.expected_impact).toUpperCase()] || '#64748b' }}>{ev.expected_impact}</div>}
                      {ev.phq_rank ? <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>Impact rank {ev.phq_rank}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* ── PI-05 Settings ── */}
      {tab === 'settings' && (
        <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
          {/* source health */}
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Source health</div>
            {health ? (
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
                <div><div style={{ color: 'var(--theme-text-muted)', fontSize: 12 }}>Demand rows</div>{health.rows ?? 0}</div>
                <div><div style={{ color: 'var(--theme-text-muted)', fontSize: 12 }}>Last computed</div>{health.last_computed ? new Date(health.last_computed).toLocaleString() : '—'}</div>
                <div><div style={{ color: 'var(--theme-text-muted)', fontSize: 12 }}>Avg confidence</div>{health.avg_confidence != null ? `${Math.round(health.avg_confidence)}%` : '—'}</div>
                {['comp', 'occupancy', 'event'].map((k) => <div key={k}><div style={{ color: 'var(--theme-text-muted)', fontSize: 12 }}>{SIGNAL_META[k].icon} {SIGNAL_META[k].label}</div>{health.coverage?.[k] ?? 0}% coverage</div>)}
              </div>
            ) : <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>—</div>}
          </div>

          {/* signal weights */}
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Signal weights</div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 10 }}>Missing or disabled signals lower confidence — never silently dropped. Recompute + regenerate to apply.</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {signals.map((s) => {
                const m = SIGNAL_META[s.signal_key] || { label: s.signal_key, icon: '•' }; const cov = health?.coverage?.[s.signal_key];
                return (
                  <div key={s.signal_key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 10px', border: '1px solid var(--theme-border-light, #f1f5f9)', borderRadius: 10 }}>
                    <div style={{ flex: '1 1 auto' }}>
                      <div style={{ fontWeight: 600 }}>{m.icon} {m.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Weight {Number(s.weight).toFixed(1)}{cov != null ? ` · coverage ${cov}%` : ''}{s.is_default ? ' · default' : ''}</div>
                    </div>
                    <input type="range" min="0" max="3" step="0.1" value={s.weight}
                      onChange={(e) => setSignals((prev) => prev.map((x) => x.signal_key === s.signal_key ? { ...x, weight: Number(e.target.value) } : x))}
                      onMouseUp={(e) => saveSignal(s.signal_key, { weight: Number(e.target.value) })} aria-label={`${m.label} weight`} style={{ width: 140 }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input type="checkbox" checked={s.enabled} onChange={(e) => saveSignal(s.signal_key, { enabled: e.target.checked })} /> On
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* baseline & guardrails */}
          {cfg && (
            <div style={card}>
              <div style={{ fontWeight: 700, marginBottom: 10 }}>Baseline & guardrails</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <label style={{ fontSize: 13 }}>Baseline
                  <select value={cfg.baseline_type} onChange={(e) => setCfg({ ...cfg, baseline_type: e.target.value })} style={{ ...sel, width: '100%', marginTop: 4 }}>
                    {(cfg.baseline_types || ['OWN_BAR', 'MARKET_MEDIAN', 'MARKET_BLEND', 'LAST_ACCEPTED']).map((b) => <option key={b} value={b}>{BASELINE_LABEL[b] || b}</option>)}
                  </select>
                </label>
                {cfg.baseline_type === 'MARKET_BLEND' && (
                  <label style={{ fontSize: 13 }}>
                    Market position — {Number(cfg.market_position_pct ?? 50)}% toward market median
                    <input type="range" min="0" max="100" step="5" value={cfg.market_position_pct ?? 50}
                      onChange={(e) => setCfg({ ...cfg, market_position_pct: Number(e.target.value) })}
                      style={{ width: '100%', marginTop: 6 }} aria-label="Market position percent" />
                    <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 2 }}>
                      0% = your own current rate, 100% = full market median. The daily-change guardrail below still limits how fast you get there each cycle.
                    </div>
                  </label>
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ fontSize: 13, flex: 1 }}>Max swing %<input type="number" value={cfg.max_swing_pct} onChange={(e) => setCfg({ ...cfg, max_swing_pct: e.target.value })} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                  <label style={{ fontSize: 13, flex: 1 }}>Max daily change %<input type="number" value={cfg.max_daily_change_pct} onChange={(e) => setCfg({ ...cfg, max_daily_change_pct: e.target.value })} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <label style={{ fontSize: 13, flex: 1 }}>Min rate ₹<input type="number" value={cfg.min_rate ?? ''} onChange={(e) => setCfg({ ...cfg, min_rate: e.target.value })} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                  <label style={{ fontSize: 13, flex: 1 }}>Max rate ₹<input type="number" value={cfg.max_rate ?? ''} onChange={(e) => setCfg({ ...cfg, max_rate: e.target.value })} style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
                </div>
                <label style={{ fontSize: 13 }}>Blackout dates (comma-separated YYYY-MM-DD)<input type="text" value={cfg.blackout_text} onChange={(e) => setCfg({ ...cfg, blackout_text: e.target.value })} placeholder="2026-08-15, 2026-10-02" style={{ ...sel, width: '100%', marginTop: 4 }} /></label>
              </div>
              <div style={{ marginTop: 14 }}><button onClick={saveConfig} style={btn('primary')}>Save config</button></div>
            </div>
          )}

          {/* REQ-3 — accepted-vs-not outcome tracking (observational, not a controlled experiment) */}
          <div style={card}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Outcome tracking (beta)</div>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 10 }}>
              Last 30 stay-dates that have already passed — accepted recommendations vs everything else. {outcomes?.note}
            </div>
            {!outcomes || (outcomes.accepted.n === 0 && outcomes.not_accepted.n === 0) ? (
              <div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>No outcomes captured yet — actuals fill in nightly once stay dates pass and night audit runs.</div>
            ) : (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {[{ key: 'accepted', label: 'Accepted' }, { key: 'not_accepted', label: 'Not accepted' }].map(({ key, label }) => {
                  const o = outcomes[key];
                  return (
                    <div key={key} style={{ flex: '1 1 160px', minWidth: 160, border: '1px solid var(--theme-border-light, #f1f5f9)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{label} ({o.n} date{o.n === 1 ? '' : 's'})</div>
                      <div style={{ fontSize: 20, fontWeight: 800 }}>{o.avg_occupancy_pct != null ? `${o.avg_occupancy_pct}%` : '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>avg occupancy{o.avg_revpar != null ? ` · RevPAR ${fmtINR(o.avg_revpar)}` : ''}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PI-03 recommendation detail drawer */}
      {reco && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setReco(null)}>
          <div role="dialog" aria-label="Recommendation detail" style={{ width: 'min(440px, 100%)', height: '100%', overflowY: 'auto', background: 'var(--theme-surface, #ffffff)', color: 'var(--theme-text, #374151)', padding: 18, boxShadow: '-4px 0 24px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div><h3 style={{ margin: 0, fontSize: 16 }}>{reco.room_name || reco.room_type_id} · {reco.meal_plan_code}</h3><div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>{reco.stay_date} · <Chip status={reco.status} /></div></div>
              <button onClick={() => setReco(null)} style={btn('ghost')} aria-label="Close">✕</button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 14px', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 10, marginBottom: 6 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{BASELINE_LABEL[reco.baseline_type] || reco.baseline_type}</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtINR(reco.baseline_rate)}</div>
                {reco.current_rate != null && Math.round(reco.current_rate) !== Math.round(reco.baseline_rate) && (
                  <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }} title="Δ and Opportunity are measured against this, not the baseline above">Current: {fmtINR(reco.current_rate)}</div>
                )}
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8' }}>→</div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Recommended</div>
                {['PENDING', 'ACCEPTED'].includes(reco.status) ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--theme-text-muted)' }}>₹</span>
                    <input type="number" value={overrideRate} onChange={(e) => setOverrideRate(e.target.value)} aria-label="Editable recommended rate"
                      style={{ width: 92, fontSize: 20, fontWeight: 800, color: 'var(--theme-primary, #0d8f83)', border: '1px solid var(--theme-border, #e5e7eb)', borderRadius: 6, padding: '2px 6px', textAlign: 'center', background: 'var(--theme-surface, #fff)' }} />
                  </div>
                ) : <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--theme-primary, #0d8f83)' }}>{fmtINR(reco.recommended_rate)}</div>}
              </div>
              <div style={{ textAlign: 'center' }}><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }} title="vs your current rate">Δ vs current</div><div style={{ fontSize: 16, fontWeight: 700, color: Number(reco.delta_pct) > 0 ? '#047857' : Number(reco.delta_pct) < 0 ? '#b91c1c' : '#64748b' }}>{reco.delta_pct != null ? `${Number(reco.delta_pct) > 0 ? '+' : ''}${Number(reco.delta_pct).toFixed(1)}%` : '—'}</div></div>
            </div>
            {['PENDING', 'ACCEPTED'].includes(reco.status) && Number(overrideRate) !== Math.round(Number(reco.recommended_rate) || 0) && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button onClick={() => saveOverride(reco.id)} disabled={overrideBusy} style={{ ...btn('ghost'), padding: '4px 10px', fontSize: 12 }}>{overrideBusy ? 'Saving…' : '✎ Save override'}</button>
              </div>
            )}
            {reco.reasoning_json?.manual_override && (
              <div style={{ fontSize: 11.5, color: '#7c3aed', background: '#f5f3ff', borderRadius: 8, padding: '6px 10px', marginBottom: 12 }}>
                ✎ Manually overridden{reco.reasoning_json.manual_override.by ? ` by ${reco.reasoning_json.manual_override.by}` : ''} — model originally recommended {fmtINR(reco.reasoning_json.manual_override.original_recommended_rate)}.
              </div>
            )}
            {reco.reasoning_json?.narrative && <div style={{ ...banner('ok'), margin: '0 0 8px' }}>{reco.reasoning_json.narrative}</div>}
            {reco.reasoning_json?.llm_narrative?.text ? (
              <div style={{ fontSize: 13, color: '#3730a3', background: '#eef2ff', borderRadius: 8, padding: '8px 10px', margin: '0 0 12px' }}>
                <span style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>✨ AI take</span>
                <div style={{ marginTop: 3 }}>{reco.reasoning_json.llm_narrative.text}</div>
              </div>
            ) : (
              <button onClick={() => generateNarrative(reco.id)} disabled={narrativeBusy} style={{ ...btn('ghost'), fontSize: 12, padding: '4px 10px', marginBottom: 12 }}>
                {narrativeBusy ? 'Asking AI…' : '✨ Generate AI narrative'}
              </button>
            )}
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 6 }}>Signal contributions · demand {reco.demand_score != null ? Math.round(reco.demand_score) : '—'}/100</div>
            {Object.keys(SIGNAL_META).map((k) => {
              const p = reco.reasoning_json?.components?.[k]; const m = SIGNAL_META[k];
              const present = p && p.present && p.value != null; const val = present ? Math.round(p.value) : null;
              return (
                <div key={k} style={{ marginBottom: 9, opacity: p && p.enabled ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{m.icon} {m.label}{p && !p.enabled ? <span style={{ color: '#94a3b8' }}> (off)</span> : null}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{present ? val : <span style={{ color: '#94a3b8' }}>missing</span>}{p ? <span style={{ color: '#94a3b8', fontSize: 11 }}> ·w{Number(p.weight).toFixed(1)}</span> : null}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 6, background: 'var(--theme-surface-2, #f1f5f9)', overflow: 'hidden', marginTop: 3 }}><div style={{ width: `${present ? val : 0}%`, height: '100%', background: band(present ? val : null).edge }} /></div>
                </div>
              );
            })}
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--theme-text-muted)' }}>
              <div style={{ fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Guardrails</div>
              <div>Multiplier ×{reco.reasoning_json?.multiplier ?? '—'} (swing ±{reco.reasoning_json?.swing_pct ?? '—'}%)</div>
              <div>Max daily change ±{reco.reasoning_json?.guardrail?.max_daily_change_pct ?? '—'}%{reco.reasoning_json?.guardrail?.min_rate != null ? ` · min ${fmtINR(reco.reasoning_json.guardrail.min_rate)}` : ''}{reco.reasoning_json?.guardrail?.max_rate != null ? ` · max ${fmtINR(reco.reasoning_json.guardrail.max_rate)}` : ''}</div>
              {reco.guardrail_bound && <div style={{ color: '#d97706', fontWeight: 600 }}>⚑ Clamped by {reco.guardrail_bound.replace(/_/g, ' ').toLowerCase()}</div>}
            </div>
            {typeof onOpenInRateShopper === 'function' && (
              <button onClick={() => onOpenInRateShopper({ date: reco.stay_date, room: reco.room_type_id, meal: reco.meal_plan_code })} style={{ ...btn('ghost'), width: '100%', marginTop: 10 }}>
                🔎 View competitor evidence in Rate Shopper →
              </button>
            )}
            {reco.status === 'PENDING' && (
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => applyRecommendation(reco.id)} style={{ ...btn('primary'), flex: 1 }} disabled={busy === `apply-${reco.id}`}>{busy === `apply-${reco.id}` ? 'Applying…' : 'Apply Recommendation'}</button>
                <button onClick={() => act(reco.id, 'accept')} style={btn('accept')}>✓ Accept</button>
                <button onClick={() => act(reco.id, 'reject')} style={btn('reject')}>✕ Reject</button>
              </div>
            )}
            {reco.status === 'ACCEPTED' && (
              <div style={{ marginTop: 16 }}>
                <button onClick={() => applyRecommendation(reco.id)} style={{ ...btn('primary'), width: '100%' }} disabled={busy === `apply-${reco.id}`}>{busy === `apply-${reco.id}` ? 'Applying…' : 'Apply Recommendation'}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* demand detail drawer (calendar) */}
      {demandDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={() => setDemandDetail(null)}>
          <div role="dialog" aria-label="Demand detail" style={{ width: 'min(420px, 100%)', height: '100%', overflowY: 'auto', background: 'var(--theme-surface, #ffffff)', color: 'var(--theme-text, #374151)', padding: 18, boxShadow: '-4px 0 24px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div><h3 style={{ margin: 0, fontSize: 16 }}>{demandDetail.stay_date}</h3><div style={{ fontSize: 13, color: 'var(--theme-text-muted)' }}>{roomName}</div></div>
              <button onClick={() => setDemandDetail(null)} style={btn('ghost')} aria-label="Close">✕</button>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{ ...kpiBox, textAlign: 'center' }}><div style={{ fontSize: 28, fontWeight: 800, color: band(demandDetail.demand_score).fg }}>{demandDetail.demand_score != null ? Math.round(demandDetail.demand_score) : '—'}</div><div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>Demand · {band(demandDetail.demand_score).label}</div></div>
              <div style={{ ...kpiBox, textAlign: 'center' }}><div style={{ fontSize: 28, fontWeight: 800 }}>{demandDetail.confidence != null ? `${Math.round(demandDetail.confidence)}%` : '—'}</div><div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>Confidence</div></div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', marginBottom: 6 }}>Signal contributions</div>
            {Object.keys(SIGNAL_META).map((k) => {
              const p = demandDetail.components?.[k]; const m = SIGNAL_META[k];
              const present = p && p.present && p.value != null; const val = present ? Math.round(p.value) : null;
              return (
                <div key={k} style={{ marginBottom: 10, opacity: p && p.enabled ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span>{m.icon} {m.label}{p && !p.enabled ? <span style={{ color: '#94a3b8' }}> (off)</span> : null}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{present ? val : <span style={{ color: '#94a3b8' }}>missing</span>}</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 6, background: 'var(--theme-surface-2, #f1f5f9)', overflow: 'hidden', marginTop: 3 }}><div style={{ width: `${present ? val : 0}%`, height: '100%', background: band(present ? val : null).edge }} /></div>
                </div>
              );
            })}
            <div style={{ marginTop: 14, fontSize: 12, color: 'var(--theme-text-muted)' }}>
              {demandDetail.comp_median != null && <div>Market median: {fmtINR(demandDetail.comp_median)}{demandDetail.comp_rank ? ` · own rank ${demandDetail.comp_rank}` : ''}</div>}
              {demandDetail.occupancy_on_books != null && <div>Projected occupancy: {Math.round(demandDetail.occupancy_on_books)}%</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PriceIntelligence;
